import React from 'react';
import { Menu, Item, Separator } from 'react-contexify';
import 'react-contexify/ReactContexify.css'; // Import default styles
import { useSelector } from 'react-redux';
import { getSuperUiConfigValueByName } from '../store/selectors/app.selector';
import { CONFIGNAME } from '../types/global.enum';

// Define unique IDs for the menus
export const ROOT_FOLDER_MENU_ID = "root-folder-context-menu";
export const REGULAR_FOLDER_MENU_ID = "regular-folder-context-menu";
export const PROJECT_MENU_ID = "project-context-menu"; // New ID for project menu
export const TESTCASE_MENU_ID = "testcase-context-menu"; // New ID for test case menu

interface FolderMenusProps {
  onNewFolder: (nodeId: string) => void;
  onCreateProject: (nodeId: string) => void;
  onRenameFolder: (nodeId: string) => void;
  onRenameProject: (projectId: number, parentId?: number) => void;
  onDeleteFolder: (nodeId: string) => void;
  onCopyProjects: (projectIds: number[]) => void;
  onCutProjects: (projectIds: number[], srcFolderId: number) => void;
  onCopyTestCases: (testCaseIds: number[], sourceProjectId: number) => void;
  onPasteProject: (nodeId: string) => void;
  onDeleteProjects: (projectIds: number[]) => void;
  onExportProjects: (projectIds: number[]) => void;
  onImportProject: (nodeId: string) => void;
  onEditDefaultClid: (nodeId: string) => void;
  onOpenAllSchedules: () => void;
  onOpenProject: (projectId: number) => void;
  onOpenTestCase: (projectId: number, testCaseId: number) => void;
  onOpenTestCaseInNewTab: (projectId: number, testCaseId: number) => void;
  onOpenTestCaseResults: (projectId: number, testCaseId: number) => void;
  onOpenScheduler: (projectId: number) => void;
  onOpenScheduledTestResults: (projectId: number) => void;
  onOpenTestResults: (projectId: number) => void;
  onRefreshAll: () => void;
  onRefreshProjectTestCases: (projectId: number) => void;
  onOpenAutoRefreshSettings: () => void;
  cutProjectCount: number;
  copiedProjectCount: number;
  copiedTestCaseCount: number;
}

// This component defines ALL menu structures
export const FolderMenus: React.FC<FolderMenusProps> = ({
  onNewFolder,
  onCreateProject,
  onRenameFolder,
  onRenameProject,
  onDeleteFolder,
  onCopyProjects,
  onCutProjects,
  onCopyTestCases,
  onDeleteProjects,
  onExportProjects,
  onImportProject,
  onEditDefaultClid,
  onOpenAllSchedules,
  onPasteProject,
  onOpenProject,
  onOpenTestCase,
  onOpenTestCaseInNewTab,
  onOpenTestCaseResults,
  onOpenScheduler,
  onOpenScheduledTestResults,
  onOpenTestResults,
  onRefreshAll,
  onRefreshProjectTestCases,
  onOpenAutoRefreshSettings,
  cutProjectCount,
  copiedProjectCount,
  copiedTestCaseCount,
}) => {
  const schedulerUiConfig = (useSelector(getSuperUiConfigValueByName(CONFIGNAME.FEATURE_MONITORING_UI)) || 'show').toLowerCase();
  const isSchedulerHidden = schedulerUiConfig === 'hide';
  const isSchedulerDisabled = schedulerUiConfig === 'greyout';
  const projectCountToPaste = cutProjectCount > 0 ? cutProjectCount : copiedProjectCount;
  const canPasteProjects = projectCountToPaste > 0;
  const canPasteTestCases = copiedTestCaseCount > 0;

  return (
    <>
      {/* Menu for the Root Folder */}
      <Menu
        id={ROOT_FOLDER_MENU_ID}
        theme="light"
        animation="fade"
      >
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onNewFolder(props.nodeId as string);
            }
          }}
        >
          New Folder
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onRenameFolder(props.nodeId as string);
            }
          }}
          disabled={true}
        >
          Rename Folder
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onEditDefaultClid(props.nodeId as string);
            }
          }}
        >
          Edit Default CLID
        </Item>
        <Separator />
        <Item
          onClick={() => {
            onOpenAutoRefreshSettings();
          }}
        >
          Auto-Refresh Settings...
        </Item>
        <Item
          onClick={() => {
            onOpenAllSchedules();
          }}
        >
          All Schedules
        </Item>
        <Item
          onClick={() => {
            onRefreshAll();
          }}
          style={{ color: '#1976d2' }}
        >
          Refresh All
        </Item>
      </Menu>

      {/* Menu for Regular Folders */}
      <Menu
        id={REGULAR_FOLDER_MENU_ID}
        theme="light"
        animation="fade"
      >
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onNewFolder(props.nodeId as string);
            }
          }}
        >
          New Folder
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onCreateProject(props.nodeId as string);
            }
          }}
        >
          Add Project
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onImportProject(props.nodeId as string);
            }
          }}
        >
          Import Project
        </Item>
        <Separator />
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onEditDefaultClid(props.nodeId as string);
            }
          }}
        >
          Edit Default CLID
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onRenameFolder(props.nodeId as string);
            }
          }}
        >
          Rename Folder
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onDeleteFolder(props.nodeId as string);
            }
          }}
          style={{ color: 'red' }}
        >
          Delete Folder
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onPasteProject(props.nodeId as string);
            }
          }}
          disabled={!canPasteProjects}
          hidden={!canPasteProjects || projectCountToPaste > 1}
        >
          Paste Project Here
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.nodeId) {
              onPasteProject(props.nodeId as string);
            }
          }}
          disabled={!canPasteProjects}
          hidden={!canPasteProjects || projectCountToPaste <= 1}
        >
          Paste Projects Here
        </Item>
      </Menu>

      {/* Menu for Projects */}
      <Menu
        id={PROJECT_MENU_ID}
        theme="light"
        animation="fade"
      >
        {/* Add Refresh Test Cases (Singular Only) */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props.projectIds.length === 1) {
              onRefreshProjectTestCases(props.projectIds[0] as number);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length !== 1}
          style={{ color: '#1976d2' }}
        >
          Refresh Test Cases
        </Item>
        {/* Rename Project */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props.projectIds.length === 1) {
              onRenameProject(
                props.projectIds[0] as number,
                props.parentId as number,
              );
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length !== 1}
        >
          Rename Project
        </Item>
        {/* Scheduler (Singular Only) */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props.projectIds.length === 1) {
              onOpenScheduler(props.projectIds[0] as number);
            }
          }}
          hidden={({ props }: any) => isSchedulerHidden || !props || props.projectIds.length !== 1}
          disabled={isSchedulerDisabled}
        >
          Scheduler
        </Item>
        {/* Scheduled Test Results (Singular Only) */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props.projectIds.length === 1) {
              onOpenScheduledTestResults(props.projectIds[0] as number);
            }
          }}
          hidden={({ props }: any) => isSchedulerHidden || !props || props.projectIds.length !== 1}
          disabled={isSchedulerDisabled}
        >
          Scheduled Test Results
        </Item>
        {/* Manual Test Results (Singular Only) */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props.projectIds.length === 1) {
              onOpenTestResults(props.projectIds[0] as number);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length !== 1}
        >
          Manual Test Results
        </Item>
        {/* Singular Copy */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds) {
              onCopyProjects(props.projectIds as number[]);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length > 1}
        >
          Copy Project
        </Item>
        {/* Plural Copy */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds) {
              onCopyProjects(props.projectIds as number[]);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length <= 1}
        >
          Copy Projects
        </Item>
        {/* Singular Cut */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props?.parentId !== undefined) {
              onCutProjects(props.projectIds as number[], props.parentId as number);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length > 1 || props.parentId === undefined}
        >
          Cut Project
        </Item>
        {/* Plural Cut */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props?.parentId !== undefined) {
              onCutProjects(props.projectIds as number[], props.parentId as number);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length <= 1 || props.parentId === undefined}
        >
          Cut Projects
        </Item>
        {/* Singular Export */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds) {
              onExportProjects(props.projectIds as number[]);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length > 1}
        >
          Export Project
        </Item>
        {/* Plural Export */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds) {
              onExportProjects(props.projectIds as number[]);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length <= 1}
        >
          Export Projects
        </Item>
        {/* Singular Delete */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds) {
              onDeleteProjects(props.projectIds as number[]);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length > 1}
          style={{ color: 'red' }}
        >
          Delete Project
        </Item>
        {/* Plural Delete */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds) {
              onDeleteProjects(props.projectIds as number[]);
            }
          }}
          hidden={({ props }: any) => !props || props.projectIds.length <= 1}
          style={{ color: 'red' }}
        >
          Delete Projects
        </Item>
        <Separator />
        {/* Paste Test Cases (Singular Only) */}
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props.projectIds.length === 1) {
              onPasteProject(`project-${props.projectIds[0]}`);
            }
          }}
          disabled={!canPasteTestCases}
          hidden={({ props }: any) => !props || props.projectIds.length !== 1 || !canPasteTestCases || copiedTestCaseCount > 1}
        >
          Paste Test Case Here
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.projectIds && props.projectIds.length === 1) {
              onPasteProject(`project-${props.projectIds[0]}`);
            }
          }}
          disabled={!canPasteTestCases}
          hidden={({ props }: any) => !props || props.projectIds.length !== 1 || !canPasteTestCases || copiedTestCaseCount <= 1}
        >
          Paste Test Cases Here
        </Item>
      </Menu>

      {/* Menu for Test Cases */}
      <Menu
        id={TESTCASE_MENU_ID}
        theme="light"
        animation="fade"
      >
        <Item
          onClick={({ props }) => {
            if (props?.projectId && props?.testCaseId) {
              onOpenTestCaseInNewTab(props.projectId as number, props.testCaseId as number);
            }
          }}
        >
          Open in New Tab
        </Item>
        <Item
          onClick={({ props }) => {
            if (props?.projectId && props?.testCaseId) {
              onOpenTestCaseResults(props.projectId as number, props.testCaseId as number);
            }
          }}
        >
          Run Results
        </Item>
        <Separator />
        <Item
          onClick={({ props }) => {
            if (props?.projectId && props?.testCaseId) {
              onCopyTestCases([props.testCaseId as number], props.projectId as number);
            }
          }}
        >
          Copy Test Case
        </Item>
      </Menu>
    </>
  );
};

// We no longer export the old component
// export default FolderContextMenu; 
